interface Props {
  date: string;
  onChange: (date: string) => void;
  min?: string;
  max?: string;
  error?: string;
  // Number of distinct flocks that already have at least one daily-log
  // entry on the server for this date. 0 = fresh entry; >0 = edit mode
  // (pre-filled, submitting will overwrite).
  existingCount?: number;
  prefilling?: boolean;
  // Size of the wizard's edit window in days (EDIT_WINDOW_DAYS from
  // DailyReportPage). Used for the static helper text so the copy stays in
  // sync with the validation logic — required so it can never silently drift
  // from the enforced window via a stale default.
  windowDays: number;
  /** True when one or more of the "what's already logged for this date"
   *  lookups failed. The later steps then show blanks that mean "couldn't
   *  read", not "nothing recorded" — and those writes are upserts, so
   *  re-entering over a blank REPLACES what is stored. Measurements handle
   *  this themselves by locking; feed, eggs and mortality cannot (they are
   *  required fields), so the honest thing is to say so here. */
  logsLoadFailed: boolean;
}

export default function StepDate({
  date,
  onChange,
  min,
  max,
  error,
  existingCount = 0,
  prefilling,
  windowDays,
  logsLoadFailed,
}: Props) {
  return (
    <div>
      <h4 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
        Record Date
      </h4>
      <div className="max-w-xs">
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Date *
        </label>
        <input
          type="date"
          value={date}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
          className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
          {/* The window is 1000 days, and "the last 1000 days" read like a
              bug to the owner (live walk, 2026-10-31). Named only when short. */}
          {windowDays > 366
            ? "Today, or an earlier day you missed."
            : `You can log or edit entries for the last ${windowDays} days (including today). Older dates can only be edited by the owner.`}
        </p>
      </div>

      {logsLoadFailed && (
        <div className="mt-4 max-w-md rounded-lg bg-warning-50 px-4 py-3 text-xs text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
          <span className="font-medium">
            Couldn't check what's already been entered for this date.
          </span>{" "}
          The next steps may look blank even if figures were saved earlier, and
          re-entering them will replace what's stored — egg trays and the
          broken-egg count included. If you're editing an existing day rather
          than entering a new one, reload the page before continuing.
        </div>
      )}

      {error && (
        <div className="mt-4 max-w-md rounded-lg bg-warning-50 px-4 py-3 text-sm text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
          {error}
        </div>
      )}

      {prefilling && !error && (
        <div className="mt-4 max-w-md text-xs text-gray-500 dark:text-gray-400">
          Checking for existing entries…
        </div>
      )}

      {!prefilling && !error && existingCount > 0 && (
        <div className="mt-4 max-w-md rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
          {existingCount} {existingCount === 1 ? "flock" : "flocks"} already
          {existingCount === 1 ? " has" : " have"} entries for this date —
          values are pre-filled. Submitting will overwrite the existing
          mortality, eggs, and feed-consumed values.
        </div>
      )}
    </div>
  );
}
